
import React, { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import ProfileAvatar from './ProfileAvatar';
import ImageCropper from './ImageCropper';


interface AvatarUploadProps {
  url: string | null;
  onUpload: (url: string) => void;
}

const AvatarUpload: React.FC<AvatarUploadProps> = ({ url, onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { toast } = useToast();
  

  const handleFileSelected = (file: File) => {
    const imageUrl = URL.createObjectURL(file);
    setSelectedImage(imageUrl);
    setShowCropDialog(true);
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }
      handleFileSelected(event.target.files[0]);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUploadClick = () => {
    document.getElementById('single-avatar-upload')?.click();
  };

  const handleCrop = async (blob: Blob) => {
    try {
      const url = await uploadAvatar(blob);
      if (url) {
        onUpload(url);
      }
      setShowCropDialog(false);
      setSelectedImage(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const uploadAvatar = async (blob: Blob): Promise<string> => {
    try {
      setUploading(true);
      window.dispatchEvent(new Event('fileUploadStart'));

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const fileName = `${user.id}/${Math.random()}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(fileName, blob);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: data.publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Avatar updated successfully",
      });

      return data.publicUrl;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    } finally {
      setUploading(false);
      window.dispatchEvent(new Event('fileUploadEnd'));
    }
  };

  return (
    <>
      <ProfileAvatar
        url={url}
        onUploadClick={handleUploadClick}
        uploading={uploading}
      />
      <input
        type="file"
        id="single-avatar-upload"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/heic,image/heif"
        onChange={handleImageSelect}
        className="hidden"
      />
      <ImageCropper
        open={showCropDialog}
        onOpenChange={setShowCropDialog}
        selectedImage={selectedImage}
        onCrop={handleCrop}
        uploading={uploading}
      />
    </>
  );
};

export default AvatarUpload;
